<template>
  <div
    v-if="workflow.canWrite"
    ref="root"
    class="flex justify-end"
    @click.stop
    @mousedown.stop
    @keydown="onKeydown"
    @keyup="onKeyup"
  >
    <UIDropdown
      :id="menuId"
      trigger="click"
      placement="bottom-end"
      :show="open"
      :options="workflowActionOptions"
      @update:show="onUpdateShow"
      @select="handleSelect"
    >
      <WorkflowIconButton
        :id="`wf-actions-trigger-${workflow.id}`"
        class="workflow-actions-cell__trigger"
        :label="t('common.moreActions')"
        :show-title="false"
        aria-haspopup="menu"
        :aria-expanded="String(open)"
        :aria-controls="menuId"
        :aria-activedescendant="activeItemId"
      >
        <DotsVerticalIcon class="workflow-actions-cell__icon" />
      </WorkflowIconButton>
    </UIDropdown>
  </div>
</template>

<script setup lang="ts">
import type { LinkedWorkflow } from "@/types/linkedWorkflows";
import { DotsVerticalIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIDropdown } from "@gohighlevel/ghl-ui";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import WorkflowIconButton from "./WorkflowIconButton.vue";
import {
  WORKFLOW_ACTION_KEYS,
  workflowActionItemId,
  workflowActionOptions,
} from "./workflowActionOptions";

const props = defineProps<{ workflow: LinkedWorkflow }>();

const emit = defineEmits<{
  rename: [];
  delete: [];
}>();

const { t } = useI18n();

const root = ref<HTMLElement | null>(null);
const open = ref(false);
/** Mirrors naive's keyboard highlight (it moves on the same ArrowUp/Down presses) */
const activeIndex = ref<number | null>(null);
/** The Space keyup after a Space activation must not click the kebab open again */
let swallowSpaceKeyup = false;

const menuId = computed(() => `wf-actions-menu-${props.workflow.id}`);
const activeItemId = computed(() => {
  const key = activeIndex.value === null ? undefined : WORKFLOW_ACTION_KEYS[activeIndex.value];
  return open.value && key ? workflowActionItemId(key) : undefined;
});

const trigger = () => root.value?.querySelector<HTMLButtonElement>("button") ?? null;

// UIDropdown forwards no attrs to the teleported menu, so its role is set once it renders
watch(open, async (isOpen) => {
  activeIndex.value = null;
  if (!isOpen) return;
  await nextTick();
  document.getElementById(menuId.value)?.setAttribute("role", "menu");
});

function onUpdateShow(show: boolean) {
  open.value = show;
}

/**
 * naive's dropdown handles ArrowUp/Down, Enter and Escape from a window listener, so keys
 * may only be held back from the row while the menu is closed.
 */
function onKeydown(event: KeyboardEvent) {
  if (!open.value) {
    event.stopPropagation();
    return;
  }
  const last = WORKFLOW_ACTION_KEYS.length - 1;
  const current = activeIndex.value;
  if (event.key === "ArrowDown") activeIndex.value = current === null ? 0 : Math.min(current + 1, last);
  else if (event.key === "ArrowUp") activeIndex.value = current === null ? 0 : Math.max(current - 1, 0);
  else if (event.key === "Escape") void nextTick(() => trigger()?.focus());
  else if (event.key === " ") {
    // Menu buttons activate with Space too; naive only knows Enter
    event.preventDefault();
    swallowSpaceKeyup = true;
    event.target?.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
  }
}

function onKeyup(event: KeyboardEvent) {
  if (event.key !== " " || !swallowSpaceKeyup) return;
  swallowSpaceKeyup = false;
  event.preventDefault();
}

const handleSelect = (key: string | number) => {
  if (key === "rename") emit("rename");
  else if (key === "delete") emit("delete");
};
</script>

<style scoped>
.workflow-actions-cell__icon {
  width: 16px;
  height: 16px;
}
</style>
